"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.overwritePricingPeriods = exports.overlappingPricingPeriods = exports.buildReplacementPricingPeriods = exports.getPeriodAlterationType = exports.PricingPeriodAlteration = void 0;
const diem_1 = __importDefault(require("@travelnest/diem"));
const pricingPeriods_1 = require("../../../../hooks/validation/pricingPeriods");
const lodash_1 = require("lodash");
var PricingPeriodAlteration;
(function (PricingPeriodAlteration) {
    PricingPeriodAlteration[PricingPeriodAlteration["Delete"] = 0] = "Delete";
    PricingPeriodAlteration[PricingPeriodAlteration["ModifyEndDate"] = 1] = "ModifyEndDate";
    PricingPeriodAlteration[PricingPeriodAlteration["ModifyStartDate"] = 2] = "ModifyStartDate";
    PricingPeriodAlteration[PricingPeriodAlteration["ModifyStartAndEndDates"] = 3] = "ModifyStartAndEndDates";
})(PricingPeriodAlteration = exports.PricingPeriodAlteration || (exports.PricingPeriodAlteration = {}));
const getPeriodAlterationType = (newPeriodStart, newPeriodEnd, existingPeriodStart, existingPeriodEnd) => {
    const deltaToStartOfNewPeriod = newPeriodStart.diff(existingPeriodStart);
    const deltaToEndOfNewPeriod = existingPeriodEnd.diff(newPeriodEnd);
    if (deltaToStartOfNewPeriod < 0 && deltaToEndOfNewPeriod < 0) {
        return PricingPeriodAlteration.Delete;
    }
    else if (deltaToStartOfNewPeriod > 0 && deltaToEndOfNewPeriod <= 0) {
        // Change existing period to be from start-date of existing to start-date of new
        return PricingPeriodAlteration.ModifyEndDate;
    }
    else if (deltaToStartOfNewPeriod <= 0 && deltaToEndOfNewPeriod > 0) {
        // Change existing period to be from the end date of the new period to the end date of the existing
        return PricingPeriodAlteration.ModifyStartDate;
    }
    else if (deltaToStartOfNewPeriod > 0 && deltaToEndOfNewPeriod > 0) {
        // Change existing period to be from start-date of existing to start-date of new
        // Change existing period to be from the end date of the new period to the end date of the existing
        return PricingPeriodAlteration.ModifyStartAndEndDates;
    }
    else {
        return PricingPeriodAlteration.Delete;
    }
};
exports.getPeriodAlterationType = getPeriodAlterationType;
const buildReplacementPricingPeriods = (alterationType, existingPeriod, newPeriodStartDate, newPeriodEndDate) => {
    const existingStart = new diem_1.default(existingPeriod.start_date);
    const existingEnd = new diem_1.default(existingPeriod.end_date);
    if (alterationType === PricingPeriodAlteration.ModifyEndDate) {
        // End date of existing period becomes the day before the new period starts
        const replacementExistingPeriod = Object.assign(Object.assign({}, existingPeriod), { end_date: new diem_1.default(newPeriodStartDate)
                .setDate(newPeriodStartDate.getDate() - 1)
                .toISOString() });
        return [replacementExistingPeriod];
    }
    else if (alterationType === PricingPeriodAlteration.ModifyStartDate) {
        // Start date of existing period becomes the day after the new period ends
        const replacementExistingPeriod = Object.assign(Object.assign({}, existingPeriod), { start_date: new diem_1.default(newPeriodEndDate)
                .setDate(newPeriodEndDate.getDate() + 1)
                .toISOString() });
        return [replacementExistingPeriod];
    }
    else if (alterationType === PricingPeriodAlteration.ModifyStartAndEndDates) {
        // The new period is in the middle of an existing period, so we have to:
        //   1. Create a period on the "left" (backwards in time), ending the day before the new period starts
        //   2. Create a period on the "right" (forwards in time), starting the day after the new period ends
        const leftHandPeriod = Object.assign(Object.assign({}, existingPeriod), { start_date: existingStart.toISOString(), end_date: new diem_1.default(newPeriodStartDate)
                .setDate(newPeriodStartDate.getDate() - 1)
                .toISOString() });
        const rightHandPeriod = Object.assign(Object.assign({}, existingPeriod), { start_date: new diem_1.default(newPeriodEndDate)
                .setDate(newPeriodEndDate.getDate() + 1)
                .toISOString(), end_date: existingEnd.toISOString() });
        return [leftHandPeriod, rightHandPeriod];
    }
    else {
        // Other possibility is `PricingPeriodAlteration.Delete`, which we can regard as a no-op
        // as it will be overwritten by the modifiedPeriods later in the splice operation
        return [];
    }
};
exports.buildReplacementPricingPeriods = buildReplacementPricingPeriods;
const overlappingPricingPeriods = (period, allPricing) => {
    const newPeriodStartDate = new diem_1.default(period.start_date);
    const newPeriodEndDate = new diem_1.default(period.end_date);
    return allPricing.filter((otherPP) => (0, pricingPeriods_1.datesOverlap)({ start: newPeriodStartDate, end: newPeriodEndDate }, { start: new diem_1.default(otherPP.start_date), end: new diem_1.default(otherPP.end_date) })
        && otherPP !== period);
};
exports.overlappingPricingPeriods = overlappingPricingPeriods;
const overwritePricingPeriods = (newPeriod, otherPricing, onReplacePricing) => {
    const newPeriodStartDate = new diem_1.default(newPeriod.start_date);
    const newPeriodEndDate = new diem_1.default(newPeriod.end_date);
    // Find the pricing periods that overlap the new dates
    const overlappingPeriods = (0, exports.overlappingPricingPeriods)(newPeriod, otherPricing);
    const newPricing = otherPricing.slice();
    // Loop through overlapping periods, work out if they need to be altered to accommodate the new period
    // then push any modified periods into new pricing list, before deleting the existing (overlapping) period
    for (const existingPeriod of overlappingPeriods) {
        const alterationType = (0, exports.getPeriodAlterationType)(newPeriodStartDate, newPeriodEndDate, new diem_1.default(existingPeriod.start_date), new diem_1.default(existingPeriod.end_date));
        // Create replacement periods (if applicable) then push them into the new pricing list
        const replacementPeriods = (0, exports.buildReplacementPricingPeriods)(alterationType, existingPeriod, newPeriodStartDate, newPeriodEndDate);
        newPricing.push(...replacementPeriods);
        // Remove the overlapping period as we've arranged a modified version (or current period is simply to be deleted with no replacement)
        const idx = newPricing.findIndex((p) => p.start_date === existingPeriod.start_date && p.end_date === existingPeriod.end_date);
        newPricing.splice(idx, 1);
    }
    newPricing.push(newPeriod);
    const newOtherPeriods = newPricing.filter((pp) => !(0, lodash_1.isEqual)(pp, newPeriod));
    onReplacePricing(newPricing, newPeriod, newOtherPeriods);
};
exports.overwritePricingPeriods = overwritePricingPeriods;
//# sourceMappingURL=ad-hoc-pricing-periods.js.map